import React from 'react'
import {
  getWeatherByCoordinatesByDates,
  getWeatherForPostcodeByDates,
  HOURS_24,
} from 'Utils/Weather'
import { TIME_SLOTS } from './options'
import UnshelteredIcon from './icons/UnshelteredIcon'
import ShelteredIcon from './icons/ShelteredIcon'
import EventsIcon from './icons/EventsIcon'
import { STEPS } from './options'

/**
 * Creates a weather fetcher with retry logic, caching, and rate limit handling
 * @param {Object} options
 * @param {Function} options.fetchFunction - The function to call for fetching weather
 * @param {Function} options.buildCacheKey - Function to build cache key from params
 * @param {Function} options.validateResponse - Function to validate if response is valid
 * @param {number} options.attempts - Number of retry attempts (default: 3)
 * @param {number} options.successDelay - Delay after successful request in ms (default: 100)
 * @param {number} options.rateLimitDelay - Delay after rate limit error in ms (default: 1000)
 * @returns {Function} - Fetch function with retry logic
 */
export const createWeatherFetcherWithRetry = ({
  fetchFunction,
  buildCacheKey,
  validateResponse,
  attempts = 3,
  successDelay = 100,
  rateLimitDelay = 1000,
} = {}) => {
  const weatherCache = new Map()
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

  return async params => {
    const cacheKey = buildCacheKey
      ? buildCacheKey(params)
      : JSON.stringify(params)

    // Return from cache without delay
    if (weatherCache.has(cacheKey)) {
      return weatherCache.get(cacheKey)
    }

    let lastError
    for (let i = 0; i < attempts; i++) {
      try {
        const data = await fetchFunction(params)

        // Validate response
        const isValid = validateResponse ? validateResponse(data) : !!data

        if (isValid) {
          weatherCache.set(cacheKey, data)
          // Add delay after successful API request
          await sleep(successDelay)
          return data
        }
      } catch (e) {
        lastError = e

        // Check if it's a rate limit error
        const errorMessage = (e && e.message) || (e && e.toString()) || ''
        const responseData = e && e.response && e.response.data
        const responseText =
          typeof responseData === 'string' ? responseData : ''
        const statusCode = e && e.response && e.response.status

        const isRateLimitError =
          errorMessage.includes('Maximum concurrency exceeded') ||
          errorMessage.includes('concurrency exceeded') ||
          errorMessage.toLowerCase().includes('too many requests') ||
          responseText.includes('Maximum concurrency exceeded') ||
          responseText.includes('concurrency exceeded') ||
          statusCode === 429

        // Sleep only if rate limit error
        if (isRateLimitError && i < attempts - 1) {
          console.warn(
            `⚠️ Rate limit error, waiting ${rateLimitDelay}ms before retry ${i +
              1}/${attempts - 1}`,
          )
          await sleep(rateLimitDelay)
        } else if (!isRateLimitError && i < attempts - 1) {
          console.warn(
            `❌ Request failed (not rate limit), retrying immediately ${i +
              1}/${attempts - 1}`,
          )
        }
      }
    }

    if (lastError) {
      console.error('Weather fetch failed after all retries:', lastError)
    }

    // Cache null/empty result to avoid repeated failed requests
    weatherCache.set(cacheKey, null)
    return null
  }
}

/**
 * Maps items with limited concurrency
 * @param {Array} items - Items to process
 * @param {number} concurrency - Maximum concurrent operations
 * @param {Function} mapper - Async function to map each item
 * @returns {Promise<Array>} - Array of mapped results
 */
export const mapWithConcurrency = async (items, concurrency, mapper) => {
  const results = new Array(items.length)
  let index = 0

  const worker = async () => {
    while (true) {
      const currentIndex = index++
      if (currentIndex >= items.length) break
      results[currentIndex] = await mapper(items[currentIndex], currentIndex)
    }
  }

  const workers = new Array(Math.min(concurrency, items.length))
    .fill(null)
    .map(() => worker())
  await Promise.all(workers)
  return results
}

const groupDatesIntoRanges = dates => {
  if (!dates || dates.length === 0) return []

  const sortedDates = [...dates].sort()
  const ranges = []
  let currentRange = { from: sortedDates[0], to: sortedDates[0] }

  for (let i = 1; i < sortedDates.length; i++) {
    const currentDate = new Date(sortedDates[i])
    const prevDate = new Date(sortedDates[i - 1])

    const diffTime = currentDate - prevDate
    const diffDays = diffTime / (1000 * 60 * 60 * 24)

    if (diffDays === 1) {
      currentRange.to = sortedDates[i]
    } else {
      ranges.push({ ...currentRange })
      currentRange = { from: sortedDates[i], to: sortedDates[i] }
    }
  }

  ranges.push(currentRange)

  return ranges
}

const getExtendedSelectedPostcodes = async ({
  data,
  ranges,
  city,
  cityWeather,
}) => {
  // If city weather is provided, use it for all postcodes (no individual fetches)
  if (cityWeather) {
    return data
      .filter(item => item.locations && item.locations.length > 0)
      .map(item => ({
        postcode: item.postcode,
        weather: cityWeather,
        locations: item.locations.map(location => ({
          id: location.id,
          isEvent: location.isEvent,
          isSheltered: location.isSheltered,
          typeId: 1,
        })),
      }))
  }

  // Create fetcher with retry logic using shared utility
  const fetchWeatherWithRetry = createWeatherFetcherWithRetry({
    fetchFunction: getWeatherForPostcodeByDates,
    buildCacheKey: ({
      postcode,
      ranges,
      cityId,
      morningStart,
      countryCode,
    }) => {
      const rangesKey = (ranges || []).map(r => `${r.from}:${r.to}`).join('|')
      return `${postcode},${cityId},${morningStart},${countryCode}|${rangesKey}`
    },
    validateResponse: weather => !!weather,
  })

  // Process sequentially (concurrency = 1) to avoid overwhelming API
  const extendedSelectedPostcodes = await mapWithConcurrency(
    data,
    1,
    async item => {
      const postcode = item.postcode
      const locations = item.locations

      if (locations.length) {
        const weather = await fetchWeatherWithRetry({
          postcode,
          ranges,
          cityId: city.id,
          morningStart: city.morningCheckInTime,
          countryCode: city.country.code,
        })

        return {
          postcode,
          weather,
          locations: locations.map(location => ({
            id: location.id,
            isEvent: location.isEvent,
            isSheltered: location.isSheltered,
            typeId: 1,
          })),
        }
      }

      return null
    },
  )

  return extendedSelectedPostcodes.filter(item => item !== null)
}

const getExtendedWhitelistedLocations = async ({ data, ranges, city }) => {
  // Create fetcher with retry logic
  const fetchWeatherWithRetry = createWeatherFetcherWithRetry({
    fetchFunction: getWeatherByCoordinatesByDates,
    buildCacheKey: ({ latitude, longitude, cityId, ranges, morningStart }) => {
      const rangesKey = (ranges || []).map(r => `${r.from}:${r.to}`).join('|')
      return `${latitude},${longitude},${cityId},${morningStart}|${rangesKey}`
    },
    validateResponse: weather => weather && Object.keys(weather).length > 0,
  })

  const extendedSelectedPostcodes = await mapWithConcurrency(
    data,
    1, // limit to 1 concurrent request to avoid rate limit
    async item => {
      const { id, isEvent, isSheltered, typeId, postcode } = item

      const weather = await fetchWeatherWithRetry({
        latitude: item.latitude,
        longitude: item.longitude,
        cityId: city.id,
        ranges,
        morningStart: city.morningCheckInTime,
      })

      return {
        id,
        isEvent,
        isSheltered,
        typeId,
        postcode,
        weather,
      }
    },
  ).then(results => results.filter(result => result !== null))

  return extendedSelectedPostcodes
}

const formatTime = time => {
  const [hours, minutes] = time.split(':')
  return `${hours}:${minutes}`
}

const getPayloadForAI = async ({
  city,
  campaign,
  selectedDates,
  preparedMissionsDatesData,
  selectedPostcodes,
  postcodesWithLocations,
  whitelistedLocations,
}) => {
  const rangesToGetWeather = groupDatesIntoRanges(selectedDates)

  const dates = preparedMissionsDatesData.reduce((acc, item) => {
    acc[item.id] = {
      morningMissions: item.morningMissions,
      lunchMissions: item.lunchMissions,
      afternoonMissions: item.afternoonMissions,
    }

    return acc
  }, {})

  // When no postcodes are selected, fetch city level weather once
  // and use it for all locations instead of fetching per location
  const useGlobalCityWeather = selectedPostcodes.length === 0
  let cityWeather = null
  if (useGlobalCityWeather && city.latitude && city.longitude) {
    const fetchCityWeatherWithRetry = createWeatherFetcherWithRetry({
      fetchFunction: getWeatherByCoordinatesByDates,
      buildCacheKey: ({
        latitude,
        longitude,
        cityId,
        ranges,
        morningStart,
      }) => {
        const rangesKey = (ranges || []).map(r => `${r.from}:${r.to}`).join('|')
        return `city_${latitude},${longitude},${cityId},${morningStart}|${rangesKey}`
      },
      validateResponse: weather => weather && Object.keys(weather).length > 0,
    })

    cityWeather = await fetchCityWeatherWithRetry({
      latitude: city.latitude,
      longitude: city.longitude,
      cityId: city.id,
      ranges: rangesToGetWeather,
      morningStart: city.morningCheckInTime,
    })
  }
  const extendedSelectedPostcodes = await getExtendedSelectedPostcodes({
    data:
      selectedPostcodes.length > 0 ? selectedPostcodes : postcodesWithLocations,
    ranges: rangesToGetWeather,
    city,
    cityWeather,
  })

  const extendedWhitelistedLocations = await getExtendedWhitelistedLocations({
    data: whitelistedLocations,
    ranges: rangesToGetWeather,
    city,
  })

  const payload = {
    cityId: city.id,
    cityName: city.name,
    campaignId: campaign.id,
    morningStart: formatTime(city.morningCheckInTime),
    lunchMissionsStart: formatTime(city.lunchCheckInTime),
    afternoonStart: formatTime(city.afternoonCheckInTime),
    dates,
    selectedPostcodes: extendedSelectedPostcodes,
    whitelistedLocations: extendedWhitelistedLocations,
  }

  return payload
}

// Function for converting time from "08:45" to "08:45 AM - 11:45 AM"
const formatTimeRange = timeString => {
  const [hours, minutes] = timeString.split(':').map(Number)

  // Start time
  const startHour = hours
  const startPeriod = startHour >= 12 ? 'PM' : 'AM'
  const startHour12 =
    startHour === 0 ? 12 : startHour > 12 ? startHour - 12 : startHour

  // End time (start + 3 hours, minutes stay the same)
  let endHour = hours + 3
  const endMinutes = minutes

  // Handle transition through 24 hours
  if (endHour >= 24) {
    endHour = endHour - 24
  }

  const endPeriod = endHour >= 12 ? 'PM' : 'AM'
  const endHour12 = endHour === 0 ? 12 : endHour > 12 ? endHour - 12 : endHour

  const startMinutesStr = minutes.toString().padStart(2, '0')
  const endMinutesStr = endMinutes.toString().padStart(2, '0')

  return `${startHour12
    .toString()
    .padStart(
      2,
      '0',
    )}:${startMinutesStr} ${startPeriod} - ${endHour12
    .toString()
    .padStart(2, '0')}:${endMinutesStr} ${endPeriod}`
}

const getMissionTimeslot = ({ city, time }) => {
  const { morningCheckInTime, lunchCheckInTime, afternoonCheckInTime } = city
  let timeSlot = ''

  const timeFormatted = time + ':00'

  if (morningCheckInTime <= timeFormatted && timeFormatted < lunchCheckInTime) {
    timeSlot = TIME_SLOTS.MORNING
  } else if (
    lunchCheckInTime <= timeFormatted &&
    timeFormatted < afternoonCheckInTime
  ) {
    timeSlot = TIME_SLOTS.LUNCH
  } else {
    timeSlot = TIME_SLOTS.AFTERNOON
  }

  return timeSlot
}

const formatLocations = postcodes => {
  return postcodes.reduce((acc, item) => {
    const locations = item.locations.map(location => ({
      ...location,
      postcode: item.postcode,
    }))
    acc.push(...locations)
    return acc
  }, [])
}

const getLocationIcons = location => {
  const icons = [location.isSheltered ? <ShelteredIcon /> : <UnshelteredIcon />]
  if (location.isEvent) {
    icons.push(<EventsIcon />)
  }

  return icons
}

const generateButtonLabel = step => {
  if (step === STEPS.DATE) {
    return 'NEXT'
  } else if (step === STEPS.LOCATION) {
    return 'GENERATE'
  } else if (step === STEPS.RESULTS) {
    return 'APPROVE'
  }
}

const nextButtonAvailable = ({
  step,
  preparedMissionsDatesData,
  isEditResultsStep,
  dataBeforeMissionCreation,
}) => {
  if (step === STEPS.DATE) {
    return (
      preparedMissionsDatesData.length > 0 &&
      preparedMissionsDatesData.every(
        ({ morningMissions, lunchMissions, afternoonMissions }) =>
          morningMissions > 0 || lunchMissions > 0 || afternoonMissions > 0,
      )
    )
  } else if (step === STEPS.LOCATION) {
    return true
  } else if (step === STEPS.RESULTS) {
    // Block approve if ratings threshold is not met
    const data = Array.isArray(dataBeforeMissionCreation)
      ? dataBeforeMissionCreation
      : []
    // count all missions across all days
    let totalMissionsCount = 0
    let ratedCount = 0
    let ratedDownButWithoutFeedbackCount = 0
    data.forEach(day => {
      const missions = Array.isArray(day.missions) ? day.missions : []
      missions.forEach(m => {
        totalMissionsCount += 1
        if (m.rating === 'up' || (m.rating === 'down' && m.feedback)) {
          ratedCount += 1
        }
        if (m.rating === 'down' && !m.feedback) {
          ratedDownButWithoutFeedbackCount += 1
        }
      })
    })
    const requiredRatings = Math.max(1, Math.ceil(totalMissionsCount * 0.25))
    const ratingsMet = ratedCount >= requiredRatings
    return (
      (!isEditResultsStep || !data.length) &&
      ratingsMet &&
      ratedDownButWithoutFeedbackCount === 0
    )
  }
}

const splitMissionsByChunksForTLA = (missionsToGenerate, chunkSize = 20) => {
  const chunks = []
  const groupedMissions = {}

  // Group missions by locationId and dateTime
  missionsToGenerate.forEach(mission => {
    const key = `${mission.locationId}_${mission.dateTime}`
    if (!groupedMissions[key]) {
      groupedMissions[key] = []
    }
    groupedMissions[key].push(mission)
  })

  // Create initial chunks from grouped missions
  const initialChunks = []
  Object.values(groupedMissions).forEach(group => {
    initialChunks.push(group)
  })

  // Merge small chunks
  let currentChunk = []
  for (const chunk of initialChunks) {
    // If current chunk + new chunk doesn't exceed the limit, merge them
    if (currentChunk.length + chunk.length <= chunkSize) {
      currentChunk = [...currentChunk, ...chunk]
    } else {
      // If current chunk is not empty, add it to the result
      if (currentChunk.length > 0) {
        chunks.push(currentChunk)
      }
      // Add new chunk as is, without splitting it
      currentChunk = [...chunk]
    }
  }

  // Add the last chunk if it's not empty
  if (currentChunk.length > 0) {
    chunks.push(currentChunk)
  }

  return chunks
}

const simplifyWeatherData = weatherData => {
  return weatherData.map(day => {
    let hours = []
    if (day.hours && day.hours.length) {
      hours = day.hours.reduce((acc, hoursData) => {
        acc.push({
          conditions: hoursData.conditions,
          datetime: formatTime(hoursData.datetime),
        })
        return acc
      }, [])
    } else {
      hours = HOURS_24.reduce((acc, hour) => {
        acc.push({ conditions: day.conditions, datetime: hour })
        return acc
      }, [])
    }

    return {
      conditions: day.conditions,
      datetime: day.datetime,
      hours,
    }
  })
}

const prepareMissionsForFeedback = data => {
  return data
    .map(day => ({
      ...day,
      missions: (day.missions || []).map(mission => {
        const { weather, rating, ...rest } = mission
        const userRating = mission.rating === 'up' ? 1 : 0
        return { ...rest, userRating }
      }),
    }))
    .filter(day => day.missions.some(mission => mission.feedback))
}

// Build feedback output by mapping dataBeforeMissionCreation and emitting
// aiOutputData + userRating + feedback for rated missions
const buildFeedbackOutput = ({ aiRawOutput, dataBeforeMissionCreation }) => {
  if (!Array.isArray(dataBeforeMissionCreation)) {
    return []
  }

  return dataBeforeMissionCreation
    .map(day => {
      const locations = (day.missions || [])
        .filter(
          m => m.aiOutputData && (m.rating === 'up' || m.rating === 'down'),
        )
        .map(m => ({
          ...m.aiOutputData,
          userRating: m.rating === 'up' ? 1 : 0,
          feedback: m.feedback || '',
        }))
      return {
        day: day.id,
        locations,
      }
    })
    .filter(d => (d.locations || []).length > 0)
}

const prepareSkippedLocations = (message, allLocationsHashMap) => {
  try {
    if (
      message &&
      typeof message === 'string' &&
      message.includes('Skipped whitelisted locations')
    ) {
      const ids = message.split(':')[1].split(',')

      if (ids.length) {
        const skippedNames = ids
          .map(id => {
            const location = allLocationsHashMap[id.trim()]
            return location ? location.name : null
          })
          .filter(Boolean)
        if (skippedNames.length) {
          return skippedNames
        } else {
          return []
        }
      } else {
        return []
      }
    } else {
      return []
    }
  } catch (error) {
    return []
  }
}

export {
  groupDatesIntoRanges,
  getPayloadForAI,
  formatTimeRange,
  getMissionTimeslot,
  formatLocations,
  getLocationIcons,
  generateButtonLabel,
  nextButtonAvailable,
  splitMissionsByChunksForTLA,
  simplifyWeatherData,
  formatTime,
  prepareMissionsForFeedback,
  buildFeedbackOutput,
  prepareSkippedLocations,
}
